import { AUDIENCES } from "@klarity/schema";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import clsx from "clsx";
import { ChevronDown, ChevronRight, Ellipsis } from "lucide-react";
import { type DragEvent, useEffect, useId, useMemo, useState } from "react";
import { useOpenTopics } from "../../app/useOpenTopics";
import { useConfirm } from "../../components/ui/ConfirmDialog";
import { Menu } from "../../components/ui/Menu";
import { DocIcon, DocMarks, TypeChip } from "../../components/ui/misc";
import { toast } from "../../components/ui/Toast";
import { api } from "../../lib/api/endpoints";
import type { Space, TreeDoc } from "../../lib/api/models";
import { can, useMe } from "../../lib/auth";
import { qk, useTree } from "../../lib/queries";
import { MoveToDialog } from "./MoveToDialog";
import {
  ancestorsOf,
  buildTree,
  byPosition,
  type DropPlace,
  type MovePlan,
  placeFromOffset,
  planMove,
  staysInGroup,
  type TreeNode,
  visibleRows,
} from "./tree-model";

export function DocTree({
  space,
  activeId,
  onNavigate,
  marks = "type",
}: {
  space: Space;
  activeId?: string;
  onNavigate?: () => void;
  /** What follows a title: the type only (the narrow column), or the code and the type (a wide panel). */
  marks?: "type" | "full";
}) {
  const me = useMe();
  const canMove = can(me, "move");
  const { data: docs = [], isLoading } = useTree(space.id);
  const queryClient = useQueryClient();
  const { confirm, element: confirmDialog } = useConfirm();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [dragId, setDragId] = useState<string | null>(null);
  const [drop, setDrop] = useState<{ id: string; place: DropPlace } | null>(null);
  const [moving, setMoving] = useState<TreeDoc | null>(null);

  useEffect(() => {
    if (!activeId) return;
    const up = ancestorsOf(docs, activeId);
    if (up.length) setExpanded((prev) => new Set([...prev, ...up]));
  }, [activeId, docs]);

  const roots = useMemo(() => buildTree(docs), [docs]);
  const rows = useMemo(() => visibleRows(roots, expanded), [roots, expanded]);
  // Templates lists its templates by who they are written for: Klarity's own people, then clients.
  const grouped = space.kind === "templates";
  const groups = useMemo(
    () =>
      AUDIENCES.map((a) => ({
        ...a,
        rows: visibleRows(
          roots.filter((r) => r.audience === a.key),
          expanded,
        ),
      })).filter((g) => g.rows.length > 0),
    [roots, expanded],
  );
  const groupId = useId();
  // The groups fold as the Handbook's topics do, but start open: the stored set is the folded ones.
  const { open: folded, toggle: toggleGroup } = useOpenTopics(undefined, "kd.templates.folded");
  // Arriving on a template unfolds its group; folded by hand it stays folded until the next arrival.
  const activeGroup =
    grouped && activeId ? docs.find((d) => d.id === (ancestorsOf(docs, activeId)[0] ?? activeId))?.audience : undefined;
  const arrival = activeGroup ? activeId : undefined;
  const [seen, setSeen] = useState<string | undefined>(undefined);
  if (arrival !== seen) {
    setSeen(arrival);
    if (activeGroup && folded.has(activeGroup)) toggleGroup(activeGroup);
  }
  /** In Templates a row may only be put where it will then be shown: a move stays inside a group. */
  const allowed = (id: string, targetId: string, place: DropPlace) =>
    !grouped || staysInGroup(docs, id, targetId, place);

  const move = useMutation({
    mutationFn: ({ id, plan }: { id: string; plan: MovePlan }) => api.documents.move(id, plan),
    onSuccess: (_d, { plan }) => {
      if (plan.parentId) setExpanded((prev) => new Set([...prev, plan.parentId as string]));
    },
    onError: (e) =>
      toast(`The document could not be moved: ${e instanceof Error ? e.message : "unknown error"}`, "error"),
    onSettled: () => queryClient.invalidateQueries({ queryKey: qk.tree(space.id) }),
  });
  const archive = async (n: TreeDoc) => {
    const ok = await confirm({
      title: `Archive “${n.title}”?`,
      body: "The document and everything under it leave the tree and search. An admin can restore them.",
      confirmLabel: "Archive",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.documents.archive(n.id);
      toast(`“${n.title}” archived.`, "success");
    } catch (e) {
      toast(`The document could not be archived: ${e instanceof Error ? e.message : "unknown error"}`, "error");
    }
    await queryClient.invalidateQueries({ queryKey: qk.tree(space.id) });
  };
  const tryMove = (id: string, targetId: string, place: DropPlace) => {
    const plan = allowed(id, targetId, place) ? planMove(docs, id, targetId, place) : null;
    if (plan) move.mutate({ id, plan });
  };

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  if (isLoading) return <p className="px-3 py-2 text-sm text-ink-3">Loading…</p>;
  if (docs.length === 0) return <p className="px-3 py-2 text-sm text-ink-3">No documents yet.</p>;

  // What Move up and Move down step over: in Templates the top-level rows of the same group only.
  const siblingsOf = (n: TreeDoc) =>
    docs
      .filter((d) => d.parentId === n.parentId && (!grouped || n.parentId !== null || d.audience === n.audience))
      .sort(byPosition);

  const row = (n: TreeNode) => {
    const hasChildren = n.children.length > 0;
    const open = expanded.has(n.id);
    const sibs = siblingsOf(n);
    const i = sibs.findIndex((s) => s.id === n.id);
    const prev = sibs[i - 1];
    const next = sibs[i + 1];
    const onDragOver = (e: DragEvent<HTMLLIElement>) => {
      if (!dragId || dragId === n.id) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const place = placeFromOffset(e.clientY - rect.top, rect.height);
      if (!allowed(dragId, n.id, place)) return;
      e.preventDefault();
      setDrop({ id: n.id, place });
    };
    return (
      <li
        key={n.id}
        role="treeitem"
        tabIndex={-1}
        aria-label={n.code ? `${n.title} ${n.code}` : n.title}
        aria-level={n.depth + 1}
        aria-expanded={hasChildren ? open : undefined}
        aria-current={n.id === activeId ? "page" : undefined}
        draggable={canMove}
        data-drop={drop?.id === n.id ? drop.place : undefined}
        onDragStart={(e) => {
          setDragId(n.id);
          e.dataTransfer.setData("text/plain", n.id);
          e.dataTransfer.effectAllowed = "move";
        }}
        onDragEnd={() => {
          setDragId(null);
          setDrop(null);
        }}
        onDragOver={onDragOver}
        onDragLeave={() => setDrop((d) => (d?.id === n.id ? null : d))}
        onDrop={(e) => {
          e.preventDefault();
          const rect = e.currentTarget.getBoundingClientRect();
          const place = placeFromOffset(e.clientY - rect.top, rect.height);
          if (dragId) tryMove(dragId, n.id, place);
          setDragId(null);
          setDrop(null);
        }}
        className={clsx(
          "group relative flex items-center gap-1 rounded-md pr-1 text-sm",
          n.id === activeId ? "bg-green-wash font-semibold text-ink" : "text-ink hover:bg-desk",
          drop?.id === n.id && drop.place === "inside" && "outline-2 outline-orange",
          drop?.id === n.id && drop.place === "before" && "shadow-[inset_0_2px_0_var(--color-orange)]",
          drop?.id === n.id && drop.place === "after" && "shadow-[inset_0_-2px_0_var(--color-orange)]",
        )}
        style={{ paddingLeft: `${4 + n.depth * 14}px` }}
      >
        {hasChildren ? (
          <button
            type="button"
            aria-label={`${open ? "Collapse" : "Expand"} ${n.title}`}
            onClick={() => toggle(n.id)}
            className="grid h-5 w-5 place-items-center rounded text-ink-3 hover:text-ink"
          >
            {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
        ) : (
          <span className="w-5" aria-hidden="true" />
        )}
        <Link
          to="/d/$ref"
          params={{ ref: n.id }}
          onClick={onNavigate}
          className="flex min-w-0 flex-1 flex-wrap items-center gap-x-1.5 gap-y-0.5 py-1"
          draggable={false}
        >
          <span className="flex min-w-0 max-w-full items-center gap-1.5">
            <DocIcon kind={n.kind} size={14} />
            <span className="truncate">{n.title}</span>
          </span>
          {marks === "full" ? (
            <DocMarks code={n.code} kind={n.kind} />
          ) : (
            // The column is narrow: the type only. The chip's tooltip and the row's name give the code.
            <TypeChip code={n.code} kind={n.kind} />
          )}
        </Link>
        {canMove ? (
          <Menu
            label={`Actions for ${n.title}`}
            align="end"
            trigger={
              <button
                type="button"
                aria-label={`Actions for ${n.title}`}
                className="grid h-6 w-6 place-items-center rounded text-ink-3 opacity-0 group-hover:opacity-100 hover:bg-green-wash focus:opacity-100"
              >
                <Ellipsis size={14} />
              </button>
            }
            items={[
              {
                id: "up",
                label: "Move up",
                disabled: !prev,
                onSelect: () => prev && tryMove(n.id, prev.id, "before"),
              },
              {
                id: "down",
                label: "Move down",
                disabled: !next,
                onSelect: () => next && tryMove(n.id, next.id, "after"),
              },
              {
                id: "top",
                label: "Move to top level",
                disabled: n.parentId === null,
                onSelect: () =>
                  move.mutate({
                    id: n.id,
                    plan: {
                      parentId: null,
                      position: Math.max(-1, ...docs.filter((d) => d.parentId === null).map((d) => d.position)) + 1,
                    },
                  }),
              },
              // To another topic or client. A template stays in Templates.
              ...(space.kind === "templates" || n.kind === "template"
                ? []
                : [{ id: "moveto", label: "Move to…", onSelect: () => setMoving(n) }]),
              { id: "archive", label: "Archive", danger: true, onSelect: () => void archive(n) },
            ]}
          />
        ) : null}
      </li>
    );
  };

  return (
    <>
      {confirmDialog}
      {moving ? (
        <MoveToDialog
          doc={{ id: moving.id, title: moving.title, spaceId: space.id }}
          open
          onClose={() => setMoving(null)}
        />
      ) : null}
      {/* biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: ARIA tree pattern on a list */}
      <ul role="tree" aria-label={space.name} className="grid grid-cols-1 gap-px py-1">
        {grouped
          ? groups.map((g) => {
              const isOpen = !folded.has(g.key);
              return (
                <li key={g.key} role="none" className="not-first:mt-0.5">
                  {/* The row of a Handbook topic; a group has no page, so the whole row folds it. */}
                  <button
                    type="button"
                    id={`${groupId}-${g.key}`}
                    aria-expanded={isOpen}
                    aria-controls={isOpen ? `${groupId}-${g.key}-docs` : undefined}
                    onClick={() => toggleGroup(g.key)}
                    className="flex w-full items-center rounded-lg text-left text-base text-ink hover:bg-desk"
                  >
                    <span className="grid w-9 shrink-0 place-items-center text-ink-3">
                      {isOpen ? (
                        <ChevronDown size={14} aria-hidden="true" />
                      ) : (
                        <ChevronRight size={14} aria-hidden="true" />
                      )}
                    </span>
                    <span className="min-w-0 flex-1 truncate py-2 pr-2.5">{g.heading}</span>
                  </button>
                  {isOpen ? (
                    // biome-ignore lint/a11y/useSemanticElements: ARIA tree pattern, a group of tree items
                    <ul
                      role="group"
                      id={`${groupId}-${g.key}-docs`}
                      aria-labelledby={`${groupId}-${g.key}`}
                      className="ml-4 grid grid-cols-1 gap-px border-l border-line py-1 pl-1"
                    >
                      {g.rows.map(row)}
                    </ul>
                  ) : null}
                </li>
              );
            })
          : rows.map(row)}
      </ul>
    </>
  );
}
